import React, { useState } from 'react';
import { Card, Form } from 'antd';
import dal from 'dal';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { InterceptSuccess, InterceptError } from 'utils/interceptors';
import styled from 'styled-components';
import { Item } from 'components/common/form';
import { Input } from 'components/common/input';
import { PrimaryButton } from 'components/common/button';
import { hashSha256 } from 'utils/helpers/hash';
import { useStateContext } from 'store/state';
import { ActionType } from 'store/reducer';
import { useHistory } from 'react-router-dom';
import ROUTES from 'utils/constants/routes.json';

const StyledCard = styled(Card)`
    border: none;

    .ant-card-body {
        padding: 0;
        padding-left: 15%;

        @media screen and (max-width: 700px) {
            padding: 0;
        }
    }
`;

const ChangePasswordCard: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const { t } = useTranslation(['profileDetails', 'notifications']);
    const { dispatch } = useStateContext();
    const history = useHistory();

    const tryAuthorizeUser = () => {
        dal.user
            .getProfile()
            .then(res => res.data)
            .then((portalUser: IPortalUser) =>
                dispatch({
                    type: ActionType.SIGN_IN,
                    payload: { user: portalUser },
                })
            )
            .catch(() => history.push(ROUTES.login_portal));
    };

    const onFinish = async (values: any) => {
        setLoading(true);
        dal.auth
            .changePassword({
                ...values,
                oldPassword: await hashSha256(values.oldPassword),
                newPassword: await hashSha256(values.newPassword),
                confirm: await hashSha256(values.confirm),
            })
            .then(() => {
                InterceptSuccess(t('profileDetails:password_success_change'));
                tryAuthorizeUser();
            })
            .catch((err: AxiosError) => {
                switch (err.code) {
                    case '403':
                        InterceptError(t('profileDetails:invalid_password'));
                        break;
                    default:
                        InterceptError(t('notifications:error_occured'));
                        break;
                }
            })
            .finally(() => {
                form.resetFields();
                setLoading(false);
            });
    };
    return (
        <StyledCard>
            <Form
                name="change-password"
                form={form}
                layout="vertical"
                initialValues={{ remember: true }}
                onFinish={onFinish}>
                <Item
                    name="oldPassword"
                    label={t('profileDetails:previous_password')}
                    rules={[
                        {
                            required: true,
                            message: '',
                        },
                    ]}>
                    <Input type="password" placeholder={t('profileDetails:insert_old_password')} />
                </Item>
                <Item
                    name="newPassword"
                    label={t('profileDetails:new_password')}
                    rules={[
                        {
                            required: true,
                            message: '',
                        },
                    ]}
                    hasFeedback>
                    <Input type="password" placeholder={t('profileDetails:insert_new_password')} />
                </Item>
                <Item
                    name="confirm"
                    label={t('profileDetails:confirm_new_password')}
                    dependencies={['newPassword']}
                    hasFeedback
                    rules={[
                        {
                            required: true,
                            message: '',
                        },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('profileDetails:password_not_match')));
                            },
                        }),
                    ]}>
                    <Input type="password" placeholder={t('profileDetails:confirm_new_password')} />
                </Item>
                <Item>
                    <PrimaryButton style={{ width: '100%' }} loading={loading} htmlType="submit">
                        {t('profileDetails:change_password')}
                    </PrimaryButton>
                </Item>
            </Form>
        </StyledCard>
    );
};

export default ChangePasswordCard;
